import React, { useRef } from 'react';
import { ClubConditionsData } from '../../types/clubConditions';
import { formatNumber, formatCurrency } from '../../utils/formatters';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useClubConditionsDataForCustomDashboard } from '../../hooks/useClubConditionsDataForCustomDashboard';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import HorizontalTableShell from '../ui/HorizontalTableShell';

interface ClubConditionsTableProps {
  data?: ClubConditionsData[]; // Optional when used in custom dashboard
  currencySymbol?: string; // Optional when used in custom dashboard
  branchId?: number;
  timeRange?: any;
  compact?: boolean;
  // Custom dashboard specific props
  useCustomDashboardData?: boolean; // Flag to indicate if we should fetch data ourselves
  showLoading?: boolean;
}

interface TableRowData extends ClubConditionsData {
  // Calculated fields
  usedCreditRatio?: number;
  generatedCreditCountRatio?: number;
  generatedCreditAmountRatio?: number;
  participationCountRate?: number;
  participationAmountRate?: number;
}


interface TableSummaryData {
  factureSum: number;
  generatedCreditSum: number;
  sumUsedCredit: number;
  factureCount: number;
  generatedCreditCount: number;
  usedCreditCount: number;
  countUniqueRecipient: number;
  uniqueCustomerCount: number;
}

const ClubConditionsTable: React.FC<ClubConditionsTableProps> = ({ 
  data = [],
  currencySymbol,
  branchId,
  timeRange,
  compact = false,
  useCustomDashboardData = false, // Default to false to maintain backward compatibility
  showLoading = true
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  // Hooks
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { openGuide } = useChartGuide();
  
  // For custom dashboard usage, fetch data ourselves
  const { data: fetchedData, isLoading, error } = useClubConditionsDataForCustomDashboard(
    useCustomDashboardData ? branchId : undefined, 
    useCustomDashboardData ? timeRange : undefined
  );
  
  // Component metadata
  const componentId = 'club-conditions-table';
  const componentTitle = 'گزارش عملکرد ماهانه شروط باشگاه مشتریان';

  // Use fetched data when in custom dashboard mode, otherwise use passed data
  const effectiveData = useCustomDashboardData ? (fetchedData?.data || []) : data;
  const effectiveCurrencySymbol = useCustomDashboardData ? (fetchedData?.currencyMetadata?.currencySymbol || 'تومان') : currencySymbol;
  
  // Handle loading state for custom dashboard
  if (useCustomDashboardData && showLoading && isLoading) {
    return (
      <div className="card-base card-padding">
        <div className="flex items-center justify-center min-h-[350px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری جدول…"
          />
        </div>
      </div>
    );
  }
  
  // Handle error for custom dashboard
  if (useCustomDashboardData && error) {
    return (
      <div className="card-base card-padding">
        <div className="flex items-center justify-center min-h-[350px]">
          <ErrorMessage message="خطا در بارگذاری داده‌های جدول" />
        </div>
      </div>
    );
  }
  
  // Check if we have currency symbol when we need it
  if (useCustomDashboardData && !effectiveCurrencySymbol && effectiveData.length > 0) {
    return (
      <div className="card-base card-padding">
        <div className="flex items-center justify-center min-h-[350px]">
          <ErrorMessage message="خطا در دریافت واحد پولی" />
        </div>
      </div>
    );
  }

  // Sort data by year and monthNum in ascending order
  const sortedData = [...effectiveData].sort((a, b) => {
    if (a.year !== b.year) {
      return a.year - b.year;
    }
    return a.monthNum - b.monthNum;
  });

  // Calculate ratios for each row
  const tableData: TableRowData[] = sortedData.map(item => ({
    ...item,
    usedCreditRatio: item.factureSum > 0 ? (item.sumUsedCredit / item.factureSum) * 100 : 0,
    generatedCreditCountRatio: item.factureCount > 0 ? (item.generatedCreditCount / item.factureCount) * 100 : 0,
    generatedCreditAmountRatio: item.factureSum > 0 ? (item.generatedCreditSum / item.factureSum) * 100 : 0,
    participationCountRate: item.countUniqueRecipient > 0 ? (item.uniqueCustomerCount / item.countUniqueRecipient) * 100 : 0,
    participationAmountRate: item.generatedCreditSum > 0 ? (item.sumUsedCredit / item.generatedCreditSum) * 100 : 0
  }));

  const initialSummary: TableSummaryData = {
    factureSum: 0,
    generatedCreditSum: 0,
    sumUsedCredit: 0,
    factureCount: 0,
    generatedCreditCount: 0,
    usedCreditCount: 0,
    countUniqueRecipient: 0,
    uniqueCustomerCount: 0
  };

  const totals = tableData.reduce<TableSummaryData>((acc, row) => {
    acc.factureSum += row.factureSum;
    acc.generatedCreditSum += row.generatedCreditSum;
    acc.sumUsedCredit += row.sumUsedCredit;
    acc.factureCount += row.factureCount;
    acc.generatedCreditCount += row.generatedCreditCount;
    acc.usedCreditCount += row.usedCreditCount;
    acc.countUniqueRecipient += row.countUniqueRecipient;
    acc.uniqueCustomerCount += row.uniqueCustomerCount;
    return acc;
  }, { ...initialSummary });

  const rowCount = tableData.length;
  const averages: TableSummaryData = rowCount > 0 ? {
    factureSum: totals.factureSum / rowCount,
    generatedCreditSum: totals.generatedCreditSum / rowCount,
    sumUsedCredit: totals.sumUsedCredit / rowCount,
    factureCount: totals.factureCount / rowCount,
    generatedCreditCount: totals.generatedCreditCount / rowCount,
    usedCreditCount: totals.usedCreditCount / rowCount,
    countUniqueRecipient: totals.countUniqueRecipient / rowCount,
    uniqueCustomerCount: totals.uniqueCustomerCount / rowCount
  } : initialSummary;

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="table"
        dashboardSource="club-conditions"
        onInfo={() => openGuide(CHART_GUIDES.clubConditionsTable)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'club-conditions',
          componentType: 'table',
          description: 'عملکرد ماهانه شروط باشگاه مشتریان با جزئیات',
          props: { branchId, timeRange, compact, useCustomDashboardData: true },
          gridLayout: {
            containerClass: 'grid grid-cols-1 gap-6'
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />
      <div className="mb-6">
        <h3 className="title-card">{componentTitle}</h3>
        <p className="text-text-secondary text-sm mt-1 text-center">نمایش جزئیات عملکرد ماهانه شروط باشگاه مشتریان</p>
      </div>
      
      {tableData.length > 0 ? (
        <HorizontalTableShell minWidth={1100}>
          <table className={`w-full border-collapse ${compact ? 'text-sm' : ''}`} aria-label="جدول گزارش عملکرد ماهانه شروط باشگاه">
            <thead className="bg-bg-secondary border-b-2" style={{borderColor: 'var(--border-secondary)'}}>
              <tr>
                <th className="px-4 py-3 text-center text-sm font-bold text-text-primary" scope="col">ماه</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-text-primary" scope="col">مبلغ کل سفارش</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-text-primary" scope="col">مبلغ اعتبار تولید شده</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-text-primary" scope="col">مبلغ اعتبار استفاده شده</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-text-primary" scope="col">تعداد کل سفارش</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-text-primary" scope="col">تعداد اعتبار تولیدی</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-text-primary" scope="col">تعداد اعتبار مصرفی</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-text-primary" scope="col">گیرندگان یکتا</th>
                <th className="px-4 py-3 text-center text-sm font-bold text-text-primary" scope="col">مصرف‌کنندگان یکتا</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((item: TableRowData, index: number) => (
                <tr 
                  key={index} 
                  className={`border-b transition-colors ${index % 2 === 0 ? 'bg-bg-card' : 'bg-bg-secondary'} hover:bg-bg-tertiary`}
                  style={{borderColor: 'var(--border-primary)'}}
                >
                  <td className="table-cell font-medium">
                    {item.month}
                  </td>
                  <td className="table-cell">
                    {formatCurrency(item.factureSum, effectiveCurrencySymbol)}
                  </td>
                  <td className="table-cell">
                    {formatCurrency(item.generatedCreditSum, effectiveCurrencySymbol)}
                  </td>
                  <td className="table-cell">
                    {formatCurrency(item.sumUsedCredit, effectiveCurrencySymbol)}
                  </td>
                  <td className="table-cell">
                    {formatNumber(item.factureCount)}
                  </td>
                  <td className="table-cell">
                    {formatNumber(item.generatedCreditCount)}
                  </td>
                  <td className="table-cell">
                    {formatNumber(item.usedCreditCount)}
                  </td>
                  <td className="table-cell">
                    {formatNumber(item.countUniqueRecipient)}
                  </td>
                  <td className="table-cell">
                    {formatNumber(item.uniqueCustomerCount)}
                  </td>
                </tr>
              ))}
              <tr className="border-y-2 bg-primary-50 font-bold" style={{borderColor: 'var(--border-secondary)'}}>
                <td className="table-cell py-4 text-primary-900">مجموع</td>
                <td className="table-cell py-4 text-primary-900">{formatCurrency(totals.factureSum, effectiveCurrencySymbol)}</td>
                <td className="table-cell py-4 text-primary-900">{formatCurrency(totals.generatedCreditSum, effectiveCurrencySymbol)}</td>
                <td className="table-cell py-4 text-primary-900">{formatCurrency(totals.sumUsedCredit, effectiveCurrencySymbol)}</td>
                <td className="table-cell py-4 text-primary-900">{formatNumber(totals.factureCount)}</td>
                <td className="table-cell py-4 text-primary-900">{formatNumber(totals.generatedCreditCount)}</td>
                <td className="table-cell py-4 text-primary-900">{formatNumber(totals.usedCreditCount)}</td>
                <td className="table-cell py-4 text-primary-900">{formatNumber(totals.countUniqueRecipient)}</td>
                <td className="table-cell py-4 text-primary-900">{formatNumber(totals.uniqueCustomerCount)}</td>
              </tr>
              <tr className="border-b-2 bg-primary-100 font-bold" style={{borderColor: 'var(--border-secondary)'}}>
                <td className="table-cell py-4 text-primary-900">میانگین</td>
                <td className="table-cell py-4 text-primary-900">{formatCurrency(Math.round(averages.factureSum), effectiveCurrencySymbol)}</td>
                <td className="table-cell py-4 text-primary-900">{formatCurrency(Math.round(averages.generatedCreditSum), effectiveCurrencySymbol)}</td>
                <td className="table-cell py-4 text-primary-900">{formatCurrency(Math.round(averages.sumUsedCredit), effectiveCurrencySymbol)}</td>
                <td className="table-cell py-4 text-primary-900">{formatNumber(Math.round(averages.factureCount))}</td>
                <td className="table-cell py-4 text-primary-900">{formatNumber(Math.round(averages.generatedCreditCount))}</td>
                <td className="table-cell py-4 text-primary-900">{formatNumber(Math.round(averages.usedCreditCount))}</td>
                <td className="table-cell py-4 text-primary-900">{formatNumber(Math.round(averages.countUniqueRecipient))}</td>
                <td className="table-cell py-4 text-primary-900">{formatNumber(Math.round(averages.uniqueCustomerCount))}</td>
              </tr>
            </tbody>
          </table>
        </HorizontalTableShell>
      ) : (
          <div className="p-8 text-center content-tertiary">
            <p>داده‌ای برای نمایش جدول وجود ندارد</p>
          </div>
        )}
    </div>
  );
};

export default ClubConditionsTable;